import { z } from 'zod';
import { foldDiacritics } from '../slugify';

// ---------------------------------------------------------------------------
// Download
// ---------------------------------------------------------------------------

export const downloadBody = z.object({
  type: z.enum(['workspace', 'agent_instructions']),
  agent_id: z.string().uuid().optional(),
  organization_name: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

export const uploadQuery = z.object({
  type: z.enum(['agent_instructions']),
  agent_id: z.string().uuid(),
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Sign URL
// ---------------------------------------------------------------------------

/**
 * The longest a URL from `POST /api/files/sign` lives, and its life when the caller does not say.
 * The URL opens its file for whoever holds it, after the caller's own access has gone.
 */
export const SIGN_URL_MAX_EXPIRES_IN_SECONDS = 3600;

export const signUrlBody = z.object({
  bucket: z.string(),
  filePath: z.string(),
  expiresIn: z.number().int().positive().max(SIGN_URL_MAX_EXPIRES_IN_SECONDS).optional(),
});

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

/** Shared base for all download responses */
interface DownloadResponseBase {
  success: true;
  download_url: string;
  expires_in: number;
}

/** POST /download (type=workspace) */
export interface DownloadWorkspaceResponse extends DownloadResponseBase {
  summary: {
    organization?: string;
    organizations?: number;
    hubs: number;
    files: number;
  };
}

/** POST /download (type=agent_instructions) */
export interface DownloadAgentInstructionsResponse extends DownloadResponseBase {
  summary: { files: number };
}

/** POST /upload */
export interface UploadResponse {
  success: true;
  file_id: string;
  file_path: string;
}

/** POST /sign */
export interface SignUrlResponse {
  signedUrl: string;
}

// ---------------------------------------------------------------------------
// Serving a stored file
// ---------------------------------------------------------------------------

/** One media type, `type/subtype` in HTTP token characters, then nothing or its parameters. */
const SINGLE_MEDIA_TYPE = /^([!#$%&'*+.^_`|~0-9a-z-]+\/[!#$%&'*+.^_`|~0-9a-z-]+)\s*(?:;|$)/;

/**
 * The type's essence — lowercased, parameters dropped — or `''` when the value is not one plain
 * media type. A browser splits a Content-Type on commas and takes the last type it can parse
 * (Fetch's "extract a MIME type"), so to it `video/mp4;x=1,text/html` is HTML: a value read any
 * other way than it is read here is treated as unknown — sandboxed, and a download.
 */
function essenceOf(contentType: string | null | undefined): string {
  const value = (contentType ?? '').trim().toLowerCase();
  if (value.includes(',')) return '';
  return SINGLE_MEDIA_TYPE.exec(value)?.[1] ?? '';
}

/** Audio or video the browser plays in its media viewer — never an XML-based type. */
function isAudioOrVideo(essence: string): boolean {
  return /^(audio|video)\//.test(essence) && !essence.includes('xml');
}

/** An image the browser draws — never SVG or another XML-based type, which are documents. */
function isDrawnImage(essence: string): boolean {
  return essence.startsWith('image/') && !/svg|xml/.test(essence);
}

/**
 * The Content-Security-Policy of a response that must never act as a document of the origin
 * serving it — every stored file ({@link applyStoredFileHeaders}), and every backend response the
 * web relays onto the app origin. Opened at its URL, it runs no script and loads nothing: a
 * sandboxed document with an opaque origin, so a stored HTML, SVG or XML file never acts as the
 * viewer there. Audio and video alone go unsandboxed, allowed their own origin's media: the
 * browser's media viewer loads the file again from that document, and from an opaque origin the
 * load fails (Chrome refuses it as cross-origin), while a media type never renders as markup.
 * `style-src 'unsafe-inline'` keeps the browser's own viewers laid out (Chrome centers an image
 * with an inline style).
 */
export function inertResponseCsp(contentType: string | null | undefined): string {
  return isAudioOrVideo(essenceOf(contentType))
    ? "default-src 'none'; media-src 'self'; style-src 'unsafe-inline'"
    : "default-src 'none'; style-src 'unsafe-inline'; sandbox";
}

/** Exact types, besides images, audio and video, that a browser shows inertly. */
const INLINE_FILE_TYPES = new Set(['application/pdf', 'text/plain', 'text/markdown', 'application/json']);

/**
 * Whether a stored file of this Content-Type is shown in place when opened at its URL: images
 * (never SVG), audio, video, PDF, plain text, Markdown and JSON. An allowlist — HTML, SVG, XML and
 * every type not named here is a download. The web's previews fetch the bytes and never read it;
 * the mobile app's attachment viewer opens the file at its URL, so a type dropped from this list
 * stops showing there.
 */
function isInlineFileType(contentType: string | null | undefined): boolean {
  const essence = essenceOf(contentType);
  return isDrawnImage(essence) || isAudioOrVideo(essence) || INLINE_FILE_TYPES.has(essence);
}

/** A byte RFC 8187 leaves unencoded in an extended parameter value (its `attr-char`). */
const ATTR_CHAR = /^[A-Za-z0-9!#$&+.^_`|~-]$/;

/**
 * The disposition's filename parameters. A name outside ASCII has no interoperable form in
 * `filename` (RFC 6266 Appendix D): the Fetch standard's `Headers.set` throws on a character above
 * U+00FF (Node's does), and workerd sends one as raw UTF-8, which a recipient may decode as
 * ISO-8859-1. So `filename` is the name in printable ASCII (accents folded, every other character
 * and every quote or backslash `_`), and a name that form changes also rides whole, UTF-8
 * percent-encoded, in `filename*`, which a browser prefers.
 */
function dispositionFileName(fileName: string): string {
  const ascii = foldDiacritics(fileName).replace(/[^\x20-\x7e]|["\\]/gu, '_');
  if (ascii === fileName) return `filename="${ascii}"`;
  let encoded = '';
  // TextEncoder writes a lone surrogate (a name cut mid-pair) as U+FFFD, where encodeURIComponent throws.
  for (const byte of new TextEncoder().encode(fileName)) {
    const char = String.fromCharCode(byte);
    encoded += ATTR_CHAR.test(char) ? char : `%${byte.toString(16).toUpperCase().padStart(2, '0')}`;
  }
  return `filename="${ascii}"; filename*=UTF-8''${encoded}`;
}

/**
 * Sets, in place, the headers every stored-file response carries, from its `Content-Type`:
 * {@link inertResponseCsp}, `nosniff`, and a `Content-Disposition` that is `inline` only for
 * {@link isInlineFileType}. `fileName`, when known, rides as the disposition's filename
 * ({@link dispositionFileName}).
 */
export function applyStoredFileHeaders(headers: Headers, fileName?: string): void {
  const contentType = headers.get('Content-Type');
  headers.set('Content-Security-Policy', inertResponseCsp(contentType));
  headers.set('X-Content-Type-Options', 'nosniff');
  const disposition = isInlineFileType(contentType) ? 'inline' : 'attachment';
  headers.set('Content-Disposition', fileName ? `${disposition}; ${dispositionFileName(fileName)}` : disposition);
}
